import { Classification } from "@/components/Classification";
import { LoadingButton } from "@/components/LoadingButton";
import { useSearchProject } from "@/features/projects/SearchProject";
import { currencyFormatter } from "@/utils/currencyFormatter";
import styled from "@emotion/styled";
import {
  type OpportunitySearch,
  getOpportunitiesSearchKeyCreator,
  updateProject,
} from "@repo/api";
import {
  Table as SkapaTable,
  TableBody,
  Text,
  Variables,
  pinFilledIcon,
  pinIcon,
} from "@repo/skapa";
import { T, getLanguageLocal } from "@repo/translation";
import {
  LocalizedDateAndTime,
  ProjectTitle,
  getCustomerName,
  translateOpportunityType,
  translateProjectStatus,
  useSettings,
  useToast,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { mutate } from "swr";
import { unstable_serialize } from "swr/infinite";

import { CardTableLoader } from "../Components";

const CompactTable = styled(SkapaTable)`
  && tbody td {
    padding-top: ${Variables.space50};
    padding-bottom: ${Variables.space50};
  }
  && tbody td:nth-child(1),
  && tbody td:nth-child(2) {
    padding-inline-end: ${Variables.space75};
    width: 1%;
    white-space: nowrap;
  }
`;

const ClickableRow = styled.tr`
  cursor: pointer;
`;

const SecondRow = styled.div`
  color: ${Variables.colourTextAndIcon2};
`;

interface GeneratedProjectsTableProps {
  isLoading: boolean;
  projects: OpportunitySearch[];
  onClickRow: (project: OpportunitySearch) => void;
}

const Row = ({
  project,
  onClickRow,
}: {
  project: OpportunitySearch;
  onClickRow: (project: OpportunitySearch) => void;
}) => {
  const { market, language, general, customerMode } = useSettings();
  const { updateToast } = useToast();
  const { options, projectFilters } = useSearchProject();

  return (
    <ClickableRow
      onClick={() => {
        onClickRow(project);
      }}
    >
      <td>
        <LoadingButton
          size="small"
          iconOnly
          ssrIcon={project.isPinned ? pinFilledIcon : pinIcon}
          type="tertiary"
          onClick={() => {
            trackAnalytics("pin_project");

            updateProject(
              { id: project.opportunityId, isPinned: !project.isPinned },
              updateToast
            ).then(() => {
              mutate(
                unstable_serialize(
                  getOpportunitiesSearchKeyCreator(
                    projectFilters.filters,
                    options
                  )
                )
              );
            });
          }}
        />
      </td>
      <td>
        <Classification
          id={project.opportunityId.toString()}
          classification={project.classification}
        />
      </td>
      <td>
        <Text textStyle="Heading.XS">
          {getCustomerName(
            {
              firstName: project.customerFirstName,
              lastName: project.customerLastName,
              organizationName: project.organizationName,
              customerType: project.customerType,
            },
            general?.localization?.reverse_names,
            customerMode
          )}
        </Text>
        <SecondRow>
          <Text textStyle="Body.S" noWrap>
            <ProjectTitle
              title={project.title}
              customerName={project.customerName}
              organizationName={project.organizationName}
            />
          </Text>
        </SecondRow>
      </td>
      <td>
        <Text textStyle="Heading.XS">
          {translateProjectStatus(project.status)}
        </Text>
        <SecondRow>
          <Text textStyle="Body.S" noWrap>
            <T translationKey="label.updated" />
            {": "}
            <LocalizedDateAndTime date={project.updatedAt ?? ""} />
          </Text>
        </SecondRow>
      </td>
      <td>
        <Text textStyle="Heading.XS">
          {currencyFormatter({
            value: project.amount,
            currency: project.currency,
            locale: getLanguageLocal(language, market),
            showSign: true,
            disableDecimals: Boolean(general?.localization?.disable_decimals),
            fractions: 2,
          })}
        </Text>
        <SecondRow>
          <Text textStyle="Body.S" noWrap>
            {translateOpportunityType(project.opportunityType)}
          </Text>
        </SecondRow>
      </td>
      <td>
        <Text textStyle="Body.S">
          {project.followupSummary?.action[language]}
        </Text>
      </td>
    </ClickableRow>
  );
};

export const GeneratedProjectsTable = ({
  isLoading,
  projects,
  onClickRow,
}: GeneratedProjectsTableProps) => {
  if (isLoading) {
    return <CardTableLoader />;
  }
  return (
    <CompactTable fullWidth aria-label="Generated projects">
      <TableBody striped>
        {projects?.map((project) => (
          <Row
            key={project.opportunityId}
            project={project}
            onClickRow={onClickRow}
          />
        ))}
      </TableBody>
    </CompactTable>
  );
};
